import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { installClientErrorCapture } from "@/lib/client-error-capture";
import { LocaleProvider } from "@/i18n/locale-context";
import { initSentryClient, setSentryUser, setSentryRoute, captureClientException, disableSentryClient, isSentryInstalled } from "@/lib/sentry-browser";
import { deriveRouteTag } from "@/lib/sentry-route-tag";
import { getConsent, subscribeConsent } from "@/lib/telemetry-consent";
import { TelemetryConsentBanner } from "@/components/TelemetryConsentBanner";
import { Footer } from "@/components/Footer";
import { FeedbackFab } from "@/components/FeedbackFab";
import { BackToTopFab } from "@/components/BackToTopFab";
import { getSentryConfig } from "@/lib/sentry-config.functions";

import appCss from "../styles.css?url";
import { Toaster } from "@/components/ui/sonner";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  useEffect(() => {
    const route =
      typeof window !== "undefined"
        ? deriveRouteTag(window.location.pathname)
        : "(ssr)";
    // eslint-disable-next-line no-console
    console.error("[boundary:root]", {
      route,
      name: error?.name,
      message: error?.message,
      stack: error?.stack,
      props: { error, reset: typeof reset },
    });
    // eslint-disable-next-line no-console
    console.error(error);
    captureClientException(error, { boundary: "root", route });
  }, [error, reset]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { name: "theme-color", content: "#5b3df6" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "apple-mobile-web-app-title", content: "Glow Up" },
      { name: "format-detection", content: "telephone=no" },
      { title: "Glow Up Coach AI" },
      { name: "description", content: "AI-powered photo coach analyzes weekly selfies to track progress and provide personalized wellness advice." },
      { name: "author", content: "Glow Up Coach" },
      { property: "og:title", content: "Glow Up Coach AI" },
      { property: "og:description", content: "AI-powered photo coach analyzes weekly selfies to track progress and provide personalized wellness advice." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:site", content: "@Lovable" },
      { name: "twitter:title", content: "Glow Up Coach AI" },
      { name: "twitter:description", content: "AI-powered photo coach analyzes weekly selfies to track progress and provide personalized wellness advice." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/1a3293ca-ccf7-49e1-a240-19d01a6fa337/id-preview-db33dafc--6ecfab31-207c-4e25-a380-fd3a5d742556.lovable.app-1778971602748.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/1a3293ca-ccf7-49e1-a240-19d01a6fa337/id-preview-db33dafc--6ecfab31-207c-4e25-a380-fd3a5d742556.lovable.app-1778971602748.png" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
        {/* Statcounter */}
        <script
          dangerouslySetInnerHTML={{
            __html:
              'var sc_project=13240982;var sc_invisible=1;var sc_security="96c807e8";',
          }}
        />
        <script async src="https://www.statcounter.com/counter/counter.js" />
        <noscript>
          <div className="statcounter">
            <a
              title="Web Analytics Made Easy - Statcounter"
              href="https://statcounter.com/"
              target="_blank"
              rel="noreferrer"
            >
              <img
                className="statcounter"
                src="https://c.statcounter.com/13240982/0/96c807e8/1/"
                alt="Web Analytics Made Easy - Statcounter"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </a>
          </div>
        </noscript>
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <LocaleProvider>
        <AuthSync />
        <ClientErrorCaptureMount />
        <SentryMount />
        <RouteTagSync />
        <Outlet />
        <Toaster />
        <TelemetryConsentBanner />
        <FeedbackFab />
        <BackToTopFab />
        <Footer />
      </LocaleProvider>
    </QueryClientProvider>
  );
}

function ClientErrorCaptureMount() {
  useEffect(() => {
    installClientErrorCapture();
  }, []);
  return null;
}

function SentryMount() {
  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      if (cancelled) return;
      if (getConsent() !== "granted") return;
      if (isSentryInstalled()) return;
      try {
        const cfg = await getSentryConfig();
        if (cancelled) return;
        initSentryClient(cfg);
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setSentryUser({ id: session.user.id, email: session.user.email ?? undefined });
        }
      } catch {
        // never let telemetry break the app
      }
    };
    start();
    const unsubConsent = subscribeConsent((s) => {
      if (s === "granted") {
        start();
      } else {
        setSentryUser(null);
        disableSentryClient();
      }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setSentryUser({ id: session.user.id, email: session.user.email ?? undefined });
      } else {
        setSentryUser(null);
      }
    });
    return () => {
      cancelled = true;
      subscription.unsubscribe();
      unsubConsent();
    };
  }, []);
  return null;
}

function RouteTagSync() {
  const router = useRouter();
  useEffect(() => {
    const update = () => {
      if (typeof window === "undefined") return;
      setSentryRoute(deriveRouteTag(window.location.pathname));
    };
    update();
    const unsub = router.subscribe("onResolved", update);
    return () => unsub();
  }, [router]);
  return null;
}

function AuthSync() {
  const router = useRouter();
  const queryClient = useQueryClient();
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      router.invalidate();
      queryClient.invalidateQueries();
    });
    return () => subscription.unsubscribe();
  }, [router, queryClient]);
  return null;
}
